<template>
  <div>
    <Header />
    <v-alert :value="error" type="error">{{ error }}</v-alert>
    
    <!-- Enhanced Header Section -->
    <div class="enhanced-header">
      <v-container>
        <div class="header-content">
          <div class="header-title-section">
            <h1 class="page-title">
              <v-icon color="white" class="mr-3">mdi-package-variant</v-icon>
              Packlisten
            </h1>
          </div>
          
          <div class="header-actions">
            <v-btn 
              color="white darken-2" 
              large
              @click="openMulticartPlacement"
              class="placement-button mr-3"
            >
              <v-icon left>mdi-package-down</v-icon>
              Einlagern
            </v-btn>
            <v-btn 
              color="orange darken-2 mr-3" 
              large
              @click="openMulticartWizard('upper')"
              class="multicart-button"
            >
              <v-icon left>mdi-cart-variant</v-icon>
              Warenkorbregal oben
            </v-btn>
            <v-badge 
              :content="multicartBadgeCount" 
              color="red" 
              overlap
              offset-x="10"
              offset-y="10"
            >
              <v-btn 
                color="orange darken-2" 
                large
                @click="openMulticartWizard('lower')"
                class="multicart-button"
              >
                <v-icon left>mdi-cart-variant</v-icon>
                Warenkorbregal unten
              </v-btn>
            </v-badge>
          </div>
        </div>

        <!-- Control Bar -->
        <div class="controls-bar">
          <div class="controls-flex">
            <div class="input-group">
              <v-text-field
                v-model="search"
                label="Packliste suchen..."
                prepend-inner-icon="mdi-magnify"
                outlined
                dense
                clearable
                class="search-field"
                hide-details
              ></v-text-field>
            </div>

            <div class="input-group">
              <v-select
                v-model="floorFilter"
                :items="floorOptions"
                placeholder="Alle"
                prepend-inner-icon="mdi-home-floor-2"
                outlined
                dense
                class="floor-filter"
                hide-details
              ></v-select>
            </div>

            <div class="input-group">
              <v-switch
                v-model="showOnlyOverdue"
                label="Nur überfällige anzeigen"
                color="error"
                dense
                hide-details
                class="overdue-switch"
              ></v-switch>
            </div>
          </div>

          <!-- Advanced Search Section -->
          <div class="advanced-search-section mt-4">
            <v-btn
              text
              small
              color="white"
              @click="showAdvancedSearch = !showAdvancedSearch"
              class="mb-2"
            >
              <v-icon left small>{{ showAdvancedSearch ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              Erweiterte Suche
            </v-btn>

            <v-expand-transition>
              <div v-show="showAdvancedSearch" class="advanced-search-content">
                <div class="advanced-search-grid">
                  <div class="input-group">
                    <v-autocomplete
                      v-model="selectedProduct"
                      :items="availableProducts"
                      item-text="label"
                      item-value="value"
                      label="Produkt auswählen"
                      prepend-inner-icon="mdi-package-variant"
                      outlined
                      dense
                      clearable
                      class="product-select"
                      hide-details
                      :loading="loadingProducts"
                      placeholder="Tippen um zu suchen..."
                      :filter="customProductFilter"
                    >
                      <template v-slot:item="{ item }">
                        <div class="product-item">
                          <span class="product-name">{{ item.label }}</span>
                          <v-chip x-small color="primary" class="ml-2">{{ item.table_count }} Tabelle(n)</v-chip>
                        </div>
                      </template>
                    </v-autocomplete>
                  </div>

                  <div class="input-group">
                    <v-text-field
                      v-model="attributeSearchTerm"
                      label="Name/Attribut suchen..."
                      prepend-inner-icon="mdi-text-search"
                      outlined
                      dense
                      clearable
                      class="attribute-search"
                      hide-details
                      placeholder="z.B. 'Max Mustermann'"
                      @input="debouncedAttributeSearch"
                    ></v-text-field>
                  </div>

                  <div class="input-group">
                    <v-btn
                      color="white"
                      :loading="searchingAttributes"
                      :disabled="!selectedProduct || !attributeSearchTerm"
                      @click="searchByAttributes"
                      class="search-button"
                    >
                      <v-icon left>mdi-magnify</v-icon>
                      Suchen
                    </v-btn>
                  </div>
                </div>

                <!-- Search Results -->
                <div v-if="attributeSearchResults.length > 0" class="search-results mt-3">
                  <div class="results-header">
                    <span>{{ attributeSearchResults.length }} Bestellung(en) gefunden</span>
                    <v-btn text x-small color="white" @click="clearAttributeSearch">
                      <v-icon small>mdi-close</v-icon>
                      Zurücksetzen
                    </v-btn>
                  </div>
                </div>
              </div>
            </v-expand-transition>
          </div>
        </div>
      </v-container>
    </div>

    <!-- Cards Section -->
    <v-container>
      <!-- Cards Grid -->
      <div class="cards-section">
        <div class="cards-grid">
          <v-card
            v-for="i in filterItems()" 
            :key="i.name"
            :class="{ 
              'card-warning': isOlderThan24Hours(i.updated) || isOlderThan24Hours(i.oldest),
              'card-hover': true 
            }"
            class="packlist-card enhanced-card"
            @click="open(i.name)"
            elevation="3"
          >
            
            <!-- Warning Badge (if overdue) -->
            <div v-if="isOlderThan24Hours(i.updated) || isOlderThan24Hours(i.oldest)" 
                 class="warning-badge">
              <v-icon small color="white">mdi-clock-alert</v-icon>
              ÜBERFÄLLIG
            </div>

            <!-- Full Width Image at Top -->
            <div class="image-header">
              <v-img 
                :src="i.image ? i.image : 'https://files.accelery.de/packages.png'" 
                contain
                height="150"
                class="full-width-image"
              >
                <template v-slot:placeholder>
                  <div class="image-placeholder">
                    <v-icon size="32" color="grey">mdi-package-variant</v-icon>
                  </div>
                </template>
              </v-img>
            </div>

            <!-- Main Content -->
            <div class="main-content">
              <!-- Primary Information (Big & Readable) -->
              <div class="primary-info">
                <div class="packlist-title">{{ i.name }}</div>
                <div class="item-count">
                  <v-icon class="mr-2" color="primary">mdi-package-variant</v-icon>
                  <span class="count-number">{{ i.items }}</span>
                  <span class="count-label">Artikel</span>
                </div>
              </div>
              
              <!-- Secondary Information (Small Badges) -->
              <div class="secondary-info">
                <v-chip 
                  v-if="i.productionType" 
                  small
                  :color="getProductionTypeColor(i.productionType)" 
                  text-color="white" 
                  class="production-type-chip"
                >
                  {{ i.productionType }}
                </v-chip>
                <v-chip v-else small color="grey" text-color="white" class="production-type-chip">
                  Kein Typ
                </v-chip>
                
                <v-chip 
                  v-if="i.packUser"
                  small 
                  color="green" 
                  text-color="white"
                  class="user-chip"
                >
                  <v-icon left x-small>mdi-account</v-icon>
                  {{ i.packUser }}
                </v-chip>
              </div>
            </div>

            <!-- Compact Date Info -->
            <div class="compact-dates">
              <div class="date-row">
                <div class="date-item">
                  <v-icon x-small :color="isOlderThan24Hours(i.updated) ? 'error' : 'success'" class="mr-1">
                    {{ isOlderThan24Hours(i.updated) ? 'mdi-clock-alert' : 'mdi-clock-check' }}
                  </v-icon>
                  <span class="date-label">Neuest:</span>
                  <span :class="['date-value', {'date-overdue': isOlderThan24Hours(i.updated)}]">
                    {{ getTimeDisplay(i.updated) }}
                  </span>
                </div>
              </div>
              
              <div class="date-row">
                <div class="date-item">
                  <v-icon x-small :color="isOlderThan24Hours(i.oldest) ? 'error' : 'warning'" class="mr-1">
                    {{ isOlderThan24Hours(i.oldest) ? 'mdi-clock-alert' : 'mdi-clock' }}
                  </v-icon>
                  <span class="date-label">Ältest:</span>
                  <span :class="['date-value', {'date-overdue': isOlderThan24Hours(i.oldest)}]">
                    {{ getTimeDisplay(i.oldest) }}
                  </span>
                </div>
              </div>
            </div>

          </v-card>
        </div>
        
        <!-- Empty State -->
        <div v-if="filterItems().length === 0 && !loading" class="empty-state">
          <v-icon size="80" color="grey lighten-1">mdi-package-variant-closed</v-icon>
          <h3 class="grey--text text--lighten-1 mt-4">Keine Packlisten gefunden</h3>
          <p class="grey--text">Versuche deine Suchkriterien zu ändern</p>
        </div>
      </div>
    </v-container>
  </div>
</template>

<script>
import Header from "../header";
import moment from "moment";
import PacklistService from "@/services/PacklistService";
import router from "@/router";
import AuthService from "@/services/AuthService";
export default {
  name: "production",
  components: {
    Header,
  },
  data: () => ({
    user: null,
    loading: true,
    items: [],
    show: false,
    error: null,
    interval: null,
    search: "",
    showOnlyOverdue: false,
    floorFilter: "Alle",
    floorOptions: ["Alle", "OG", "EG"],
    floorMappings: {
      OG: ["co2", "laser", null],
      EG: ["diamant", "jfx", null, "uv"]
    },
    multicartBadgeCount: 0, // Will be loaded from API
    // Advanced Search
    showAdvancedSearch: false,
    availableProducts: [],
    loadingProducts: false,
    selectedProduct: null,
    attributeSearchTerm: "",
    searchingAttributes: false,
    attributeSearchResults: [],
    searchDebounceTimer: null,
    searchedTable: null
  }),
  created() {
    // URL Parameter beim Laden auslesen
    const { search, floor, overdue } = this.$route.query;
    if (search) this.search = search;
    if (floor && this.floorOptions.includes(floor)) this.floorFilter = floor;
    if (overdue) this.showOnlyOverdue = overdue === 'true';
  },
  async mounted() {
    try {
      this.user = (await AuthService.getUser()).data;
      if(this.user.role == "support") {
        return router.replace("/support");
      }
    } catch (err) {
      console.error(err);
      if (err.response.status == 401) {
        router.replace("/auth/login");
      }
    }

    await this.loadMulticartBadgeCount();
    await this.loadAvailableProducts();

    this.refresh();
    this.interval = setInterval(() => this.refresh(), 3000);
  },
  async beforeDestroy() {
    clearInterval(this.interval);
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
  },
  
  watch: {
    // URL bei Filteränderung aktualisieren
    search(newVal) {
      this.updateUrlParams();
    },
    floorFilter(newVal) {
      this.updateUrlParams();
    },
    showOnlyOverdue(newVal) {
      this.updateUrlParams();
    }
  },

  methods: {
    updateUrlParams() {
      // URL-Parameter setzen ohne Seiten-Neuladen
      const query = {};
      if (this.search) query.search = this.search;
      if (this.floorFilter !== "Alle") query.floor = this.floorFilter;
      if (this.showOnlyOverdue) query.overdue = 'true';

      router.replace({ query }).catch(err => {
        // Ignoriere Fehler, wenn die gleiche Route geladen wird
      });
    },
    
    async refresh() {
      this.loading = true;
      this.itemNames = null;
      try {
        this.items = (await PacklistService.getPacklists()).data;
        this.multicartBadgeCount = (await PacklistService.getMultiCartOrders()).data.length;
        this.loading = false;
      } catch (err) {
        console.error(err);
        if (err.response.status == 401) {
          router.replace("/auth/login");
        }
      }
    },

    filterItems() {
      if(this.items.length == 0) return [];
      return this.items.filter((i) => {
        const matchesSearch = i.name.toLowerCase().includes(this.search.toLowerCase());
        const isOverdue = this.isOlderThan24Hours(i.updated) || this.isOlderThan24Hours(i.oldest);
        const matchesFloor = this.floorFilter === "Alle" ||
          (this.floorFilter === "OG" && this.floorMappings.OG.includes(i.productionType)) ||
          (this.floorFilter === "EG" && this.floorMappings.EG.includes(i.productionType));

        // If there are attribute search results, filter by matching table names
        let matchesTable = true;
        if (this.attributeSearchResults.length > 0) {
          // Flatten all table names from search results (each result can have multiple tables separated by commas)
          const allTableNames = [];
          this.attributeSearchResults.forEach(result => {
            if (result.tables) {
              // Split comma-separated tables and trim whitespace
              const tables = result.tables.split(',').map(t => t.trim());
              // Exclude "WarenkorbRegal" from the filter
              const filteredTables = tables.filter(t => t !== 'WarenkorbRegal');
              allTableNames.push(...filteredTables);
            }
          });

          // Check if the packlist name matches any of the table names
          matchesTable = allTableNames.some(tableName =>
            i.name === tableName || i.name.startsWith(tableName)
          );
        }

        if (this.showOnlyOverdue) {
          return matchesSearch && isOverdue && matchesFloor && matchesTable;
        }

        return matchesSearch && matchesFloor && matchesTable;
      });
    },

    getProductionTypeColor(type) {
      switch(type) {
        case "co2": return "blue";
        case "laser": return "orange";
        case "diamant": return "green";
        case "jfx": return "purple";
        case "uv": return "deep-purple";
        default: return "grey";
      }
    },

    getTimeDisplay(date) {
      if (!date) return "Nicht verfügbar";
      
      const hoursDiff = moment().diff(moment(date), 'hours');
      if (hoursDiff < 24) {
        return `vor ${hoursDiff} Stunden`;
      } else {
        const daysDiff = Math.floor(hoursDiff / 24);
        return `vor ${daysDiff} Tag${daysDiff !== 1 ? 'en' : ''}`;
      }
    },

    getTimeSinceCreated(date) {
      return moment(date).fromNow();
    },

    isOlderThan24Hours(date) {
      if (!date) return false;
      return moment().diff(moment(date), 'hours') >= 24;
    },

    getDate(date) {
      return moment(date).format("DD.MM.YYYY HH:mm");
    },

    open(id) {
      clearInterval(this.interval);
      router.push({
        path: "/packlistV3/" + encodeURIComponent(id),
      });
    },

    openMulticartWizard(shelfStr) {
      clearInterval(this.interval);
      //Push to the Wizard Route
      router.push({
        path: "/multicart-wizard" + (shelfStr ? `?shelf=${shelfStr}` : '')
      });
    },

    openMulticartPlacement() {
      clearInterval(this.interval);
      router.push({
        path: "/warehouse/place"
      });
    },

    openMulticartShelfManager() {
      clearInterval(this.interval);
      router.push({
        path: "/warehouse/multicart"
      });
    },

    async loadMulticartBadgeCount() {
      try {
        const data = await PacklistService.getMultiCartOrders();
        this.multicartBadgeCount = data.data.length;
      } catch (err) {
        console.error(err);
        this.multicartBadgeCount = 0;
      }
    },

    // Advanced Search Methods
    async loadAvailableProducts() {
      this.loadingProducts = true;
      try {
        const response = await PacklistService.getSearchQueryProducts();
        this.availableProducts = response.data;
        console.log('Loaded products:', this.availableProducts.length);
      } catch (err) {
        console.error('Error loading products:', err);
        this.error = 'Fehler beim Laden der Produkte';
      } finally {
        this.loadingProducts = false;
      }
    },

    debouncedAttributeSearch() {
      // Clear existing timer
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }

      // Minimum 3 characters required
      if (!this.attributeSearchTerm || this.attributeSearchTerm.length < 3) {
        console.log('Search term too short, minimum 3 characters required');
        return;
      }

      // Set new timer for 500ms delay
      this.searchDebounceTimer = setTimeout(() => {
        this.searchByAttributes();
      }, 500);
    },

    async searchByAttributes() {
      // Validate inputs
      if (!this.selectedProduct) {
        this.error = 'Bitte wähle ein Produkt aus';
        return;
      }

      if (!this.attributeSearchTerm || this.attributeSearchTerm.length < 3) {
        this.error = 'Bitte gib mindestens 3 Zeichen ein';
        return;
      }

      this.searchingAttributes = true;
      this.error = null;

      try {
        console.log(`Searching for: "${this.attributeSearchTerm}" in product: "${this.selectedProduct}"`);

        const response = await PacklistService.searchForAttributes(
          this.attributeSearchTerm,
          this.selectedProduct
        );

        // Handle new response format with searchedTable and results
        const searchData = response.data;
        this.attributeSearchResults = searchData.results || [];
        this.searchedTable = searchData.searchedTable;

        console.log(`Found ${this.attributeSearchResults.length} results for table: ${this.searchedTable}`);
        console.log('Results:', this.attributeSearchResults);

        if (this.attributeSearchResults.length === 0) {
          this.error = 'Keine Bestellungen gefunden';
        }
        // The filterItems() method will automatically filter packlists based on attributeSearchResults
      } catch (err) {
        console.error('Error searching attributes:', err);
        this.error = 'Fehler bei der Suche';
      } finally {
        this.searchingAttributes = false;
      }
    },

    clearAttributeSearch() {
      this.attributeSearchTerm = "";
      this.selectedProduct = null;
      this.attributeSearchResults = [];
      this.searchedTable = null;
      this.error = null;
    },

    openOrderDetails(orderId) {
      console.log('Open order:', orderId);
      // Set the search field to the order ID to filter packlists
      this.search = orderId.toString();
      // Scroll to top to see the filtered results
      window.scrollTo({ top: 400, behavior: 'smooth' });
    },

    customProductFilter(item, queryText) {
      // Case-insensitive search on product label
      const searchText = queryText.toLowerCase();
      const productName = item.label.toLowerCase();
      return productName.includes(searchText);
    }
  },
};
</script>

<style scoped>
/* Enhanced Header Styles */
.enhanced-header {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  color: white;
  padding: 24px 0;
  margin-bottom: 32px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.header-title-section .page-title {
  font-size: 2.5rem;
  font-weight: 300;
  margin: 0;
  display: flex;
  align-items: center;
}

.header-actions .multicart-button,
.header-actions .placement-button {
  min-width: 200px;
  height: 48px;
  font-weight: 500;
}

.controls-bar {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 20px;
  backdrop-filter: blur(10px);
}

.controls-flex {
  display: flex;
  align-items: center;
  gap: 20px;
}

.input-group {
  flex: 1;
  display: flex;
  align-items: center;
}

.input-group:last-child {
  flex: 0 0 auto;
  justify-content: flex-end;
}

.search-field, .floor-filter {
  background: white;
  border-radius: 8px;
}

.search-field >>> .v-input__control,
.floor-filter >>> .v-input__control {
  min-height: 40px !important;
}

.search-field >>> .v-input__slot,
.floor-filter >>> .v-input__slot {
  margin-bottom: 0 !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.overdue-switch {
  color: white;
}

/* Fix switch alignment by removing default margins */
.controls-bar .overdue-switch.v-input--selection-controls {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

.controls-bar .overdue-switch.v-input--selection-controls .v-input__control {
  margin-top: 0 !important;
}

/* Enhanced Cards Section */
.cards-section {
  min-height: 400px;
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  margin-bottom: 32px;
}

.packlist-card {
  width: 100%;
  min-height: 330px;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  transition: all 0.3s ease;
  cursor: pointer;
  background: white;
}

/* Warning Badge */
.warning-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  background: #ff5252;
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: bold;
  z-index: 2;
  box-shadow: 0 2px 8px rgba(255, 82, 82, 0.3);
}

/* Full Width Image Header */
.image-header {
  height: 150px;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
}

.full-width-image {
  width: 100%;
  height: 100%;
}

.image-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  background: #f5f5f5;
}

/* Main Content Section */
.main-content {
  padding: 12px 16px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* Primary Information (Big & Important) */
.primary-info {
  text-align: center;
}

.packlist-title {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 8px;
  color: #333;
  line-height: 1.3;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
}

.item-count {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: #666;
  font-weight: 500;
}

.count-number {
  font-size: 20px;
  font-weight: bold;
  color: #1976d2;
  margin-right: 4px;
}

.count-label {
  font-size: 14px;
}

/* Secondary Information (Small Badges) */
.secondary-info {
  display: flex;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
}

.production-type-chip,
.user-chip {
  font-size: 11px !important;
  height: 22px !important;
}

/* Compact Dates */
.compact-dates {
  background: #f8f9fa;
  padding: 8px 12px;
  margin-top: auto;
}

.date-row {
  margin-bottom: 4px;
}

.date-row:last-child {
  margin-bottom: 0;
}

.date-item {
  display: flex;
  align-items: center;
  font-size: 11px;
}

.date-label {
  font-weight: 500;
  color: #666;
  margin-right: 6px;
  min-width: 40px;
}

.date-value {
  color: #333;
  font-weight: 500;
}

.date-overdue {
  color: #ff5252;
  font-weight: bold;
}

.user-info {
  display: flex;
  margin-top: auto;
  border-top: 1px solid #eee;
}

.user-section {
  flex: 1;
  padding: 8px;
}

.user-section:first-child {
  border-right: 1px solid #eee;
}

.user-label {
  font-size: 10px;
  color: #666;
  margin-bottom: 5px;
}

.user-chip {
  font-size: 11px;
}

.current-user-info {
  position: absolute;
  z-index: 1;
}

/* Enhanced Card Interactions */
.enhanced-card {
  border: 2px solid transparent;
}

.enhanced-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  border-color: #1976d2;
}

.card-warning {
  border: 2px solid #ff5252 !important;
  box-shadow: 0 4px 20px rgba(255, 82, 82, 0.3) !important;
  animation: warning-pulse 2s infinite;
}

@keyframes warning-pulse {
  0%, 100% { 
    box-shadow: 0 4px 20px rgba(255, 82, 82, 0.3);
  }
  50% { 
    box-shadow: 0 4px 25px rgba(255, 82, 82, 0.5);
  }
}

.card-warning:hover {
  border-color: #ff5252 !important;
  box-shadow: 0 8px 30px rgba(255, 82, 82, 0.4) !important;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  margin: 40px 0;
}

/* Responsive Design */
@media (max-width: 960px) {
  .header-content {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 20px;
  }
  
  .header-title-section .page-title {
    font-size: 2rem;
  }
  
  .controls-flex {
    flex-direction: column;
    gap: 16px;
  }
  
  .input-group {
    width: 100%;
  }
  
  .input-group:last-child {
    justify-content: flex-start;
  }
  
  .cards-grid {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 16px;
  }

  .packlist-card {
    min-height: 310px;
  }
}

@media (max-width: 600px) {
  .enhanced-header {
    padding: 16px 0;
    margin-bottom: 20px;
  }

  .header-title-section .page-title {
    font-size: 1.75rem;
  }

  .controls-bar {
    padding: 16px;
  }

  .cards-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .advanced-search-grid {
    grid-template-columns: 1fr !important;
  }
}

/* Advanced Search Styles */
.advanced-search-section {
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  padding-top: 16px;
}

.advanced-search-content {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 16px;
}

.advanced-search-grid {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 16px;
  align-items: center;
}

.product-select,
.attribute-search {
  background: white;
  border-radius: 8px;
}

.product-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.product-name {
  font-weight: 500;
}

.search-button {
  min-width: 120px;
  height: 40px;
}

.search-results {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 12px;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  color: white;
  font-weight: 500;
}

.results-list {
  display: flex;
  flex-wrap: wrap;
}

.clickable-chip {
  cursor: pointer;
  transition: all 0.2s ease;
}

.clickable-chip:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}
</style>
